import { DocumentsApi, TemplatesApi } from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import type { BasicAttendeeModel } from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import type { IUploadFile } from "~/utils/interfaces/documents-interfaces";

const MAX_SIZE_FILE = 10000000; // 10mo

interface IAttendee {
  id: string;
  displayValue: string;
  itemType: string;
}

interface IUploadDocumentStore {
  files: IUploadFile[];
  attendees: IAttendee[] | null;
}

export const useUploadDocumentStore = defineStore("uploadDocumentStore", {
  state: (): IUploadDocumentStore => {
    return {
      files: [],
      attendees: [],
    };
  },

  getters: {
    /**
     * Check if all files have a valid size
     */
    canUpload: (state): boolean => state.files.every((file) => file.isGoodSize),
  },

  actions: {
    /**
     * Create an uploadable file with all necessary properties
     */
    createUploadableFile(
      file: File,
      itemId: string,
      itemType: string,
      documentType: string,
      attendee?: BasicAttendeeModel,
    ): IUploadFile {
      return {
        file,
        id: `${file.name}-${file.size}-${file.lastModified}-${file.type}`,
        isGoodSize: file.size < MAX_SIZE_FILE,
        itemId,
        itemType,
        documentType,
        adminOnly: false,
        attendee,
      };
    },
    /**
     * Delete a file from S3 storage
     */
    async deleteImageFromS3(fileUrl: string): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);
      const feedback = useFeedback();

      try {
        await apiInstance.deleteFileTemplate({ deleteFileTemplateDto: { link: fileUrl } });
      } catch (error) {
        feedback.error("Erreur lors de la suppression du fichier", "small");
        throw error;
      }
    },

    /**
     * Add files to the upload list
     */
    addFiles(
      newFiles: File[],
      itemId: string,
      itemType: string,
      documentType: string,
      attendee?: BasicAttendeeModel,
    ): void {
      const newUploadableFiles = Array.from(newFiles)
        .map((file) => this.createUploadableFile(file, itemId, itemType, documentType, attendee))
        .filter((file) => !this.fileExists(file.id));

      this.files.push(...newUploadableFiles);
    },

    /**
     * Check if a file already exists in the list
     */
    fileExists(fileId: string): boolean {
      return this.files.some(({ id }) => id === fileId);
    },

    /**
     * Remove a file from the upload list
     */
    removeFile(fileToRemove: File): void {
      const index = this.files.findIndex(({ file }) => file === fileToRemove);
      if (index > -1) {
        this.files.splice(index, 1);
      }
    },

    /**
     * Update the document type of a file
     */
    updateFileDocumentType(fileId: string, documentType: string): void {
      const file = this.files.find(({ id }) => id === fileId);
      if (file) {
        file.documentType = documentType;
      }
    },

    /**
     * Update the visibility of a file
     */
    updateFileVisibility(fileId: string, adminOnly: boolean): void {
      const file = this.files.find(({ id }) => id === fileId);
      if (file) {
        file.adminOnly = adminOnly;
      }
    },

    /**
     * Update the item associated with a file
     */
    updateFileItem(fileId: string, selectedUser: BasicAttendeeModel, itemType: string): void {
      const file = this.files.find(({ id }) => id === fileId);
      if (file) {
        file.itemType = itemType;
        file.itemId = selectedUser.id;
      }
    },

    /**
     * Upload a file to S3 from the template editor
     */
    async uploadFileToS3fromTemplateEditor(file: Blob): Promise<string | undefined> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TemplatesApi);
      const feedback = useFeedback();

      try {
        const response = await apiInstance.uploadFileTemplate({ file });
        return response.url;
      } catch (error) {
        feedback.error("Erreur lors de l'upload du fichier", "small");
        throw error;
      }
    },

    /**
     * Upload a single file
     */
    async uploadFile(file: IUploadFile, itemType?: string): Promise<any> {
      if (itemType) {
        file.itemType = itemType;
      }
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
      try {
        const response = await apiInstance.uploadDocument({
          file: file.file,
          documentType: file.documentType ? file.documentType : ("other" as any),
          itemType: file.itemType ? file.itemType : ("session" as any),
          itemId: file.itemId,
          adminOnly: file.adminOnly ? "true" : "false",
        });

        return response;
      } catch (error) {
        feedback.error("Erreur lors de l'upload du fichier", "small");
        throw error;
      }
    },

    /**
     * Upload multiple files
     */
    async uploadFiles(files: IUploadFile[], itemType?: string): Promise<any[]> {
      return await Promise.all(files.map((file: IUploadFile) => this.uploadFile(file, itemType)));
    },
  },
});
